/**
 * The applicant's personal booking link (owner request 2026-11-01): the open times by day, in the
 * organization's time zone; booking one closes the link and opens the applicant's room page.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarX2, Lock } from "lucide-react";
import type { BookInterviewResponse, InterviewBookingResponse } from "@mubin/contracts";
import { errorText } from "../lib/api";
import {
  clock,
  dayKey,
  hijriDate,
  hijriDayNum,
  hijriMonth,
  hourOf,
  linkToken,
  weekday,
  withToken,
} from "../lib/interview";

export function InterviewBookPage() {
  const token = useMemo(linkToken, []);
  const page = useQuery({
    queryKey: ["interview-booking", token],
    queryFn: () => withToken<InterviewBookingResponse>("/v1/public/interview/booking", token!),
    enabled: !!token,
    retry: false,
  });
  const [day, setDay] = useState<string | null>(null);
  const [slotId, setSlotId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token || page.isError) return <Closed />;
  if (page.isPending) return <Shell loading />;
  const { name, timezone: tz, slots, bookBy } = page.data;
  const days = [...new Set(slots.map((s) => dayKey(s.startsAt, tz)))];
  const open = (d: string) => slots.some((s) => dayKey(s.startsAt, tz) === d && s.available);
  const chosenDay = day ?? days.find(open) ?? null;
  const ofDay = slots.filter((s) => dayKey(s.startsAt, tz) === chosenDay);
  const morning = ofDay.filter((s) => hourOf(s.startsAt, tz) < 12);
  const evening = ofDay.filter((s) => hourOf(s.startsAt, tz) >= 12);
  const chosen = slots.find((s) => s.id === slotId);
  const first = name.split(/\s+/)[0];

  async function book() {
    if (!chosen) return;
    setBusy(true);
    setError(null);
    try {
      const r = await withToken<BookInterviewResponse>("/v1/public/interview/booking", token!, {
        method: "POST",
        json: { slotId: chosen.id },
      });
      window.location.replace(`/interview/room#t=${r.roomToken}&booked=1`);
    } catch (e) {
      setError(errorText(e));
      setSlotId(null);
      void page.refetch();
      setBusy(false);
    }
  }

  const Slots = ({ list }: { list: typeof slots }) => (
    <div className="iv-slots">
      {list.map((s) => (
        <button
          key={s.id}
          type="button"
          className="iv-slot"
          disabled={!s.available}
          aria-pressed={s.id === slotId}
          aria-label={`${clock(s.startsAt, tz)}${s.available ? "" : "، محجوز"}`}
          onClick={() => setSlotId(s.id)}
        >
          {clock(s.startsAt, tz)}
        </button>
      ))}
    </div>
  );

  return (
    <Shell>
      <header className="iv-top">
        <img src="/logo-ivory.png" alt="مُبين" />
        <h1>حياك الله أ. {first}</h1>
        <p>اختر الوقت المناسب لمقابلتك مع الجمعية</p>
        <span className="iv-pill">
          <Lock size={13} strokeWidth={2} /> رابط خاص بك · صالح حتى {hijriDate(bookBy, tz)}
        </span>
      </header>
      <div className="iv-body">
        {error && (
          <div className="iv-error" role="alert">
            {error}
          </div>
        )}
        {days.length === 0 ? (
          <div className="iv-center">
            <div className="iv-check muted">
              <div>
                <CalendarX2 size={28} />
              </div>
            </div>
            <div className="iv-title">لا توجد مواعيد متاحة الآن</div>
            <p className="iv-sub">تُضاف مواعيد جديدة قريبًا بإذن الله، افتح هذا الرابط لاحقًا.</p>
          </div>
        ) : (
          <>
            <div className="iv-label">اليوم</div>
            <div className="iv-days" role="group" aria-label="اليوم">
              {days.map((d) => {
                const at = slots.find((s) => dayKey(s.startsAt, tz) === d)!.startsAt;
                return (
                  <button
                    key={d}
                    type="button"
                    className="iv-day"
                    disabled={!open(d)}
                    aria-pressed={d === chosenDay}
                    onClick={() => {
                      setDay(d);
                      setSlotId(null);
                    }}
                  >
                    <span>{weekday(at, tz)}</span>
                    <b>{hijriDayNum(at, tz)}</b>
                    <span>{open(d) ? hijriMonth(at, tz) : "مكتمل"}</span>
                  </button>
                );
              })}
            </div>
            {morning.length > 0 && (
              <>
                <div className="iv-period">☀️ الفترة الصباحية</div>
                <Slots list={morning} />
              </>
            )}
            {evening.length > 0 && (
              <>
                <div className="iv-period">🌙 الفترة المسائية</div>
                <Slots list={evening} />
              </>
            )}
            <p className="iv-note">الأوقات بتوقيت السعودية{localNote(tz)}</p>
          </>
        )}
      </div>
      {days.length > 0 && (
        <div className="iv-cta">
          <button type="button" disabled={!chosen || busy} onClick={() => void book()}>
            {busy ? "جارٍ الحجز…" : "تأكيد الموعد"}
            <small>
              {chosen ? `${hijriDate(chosen.startsAt, tz)} · ${clock(chosen.startsAt, tz)}` : "اختر وقتًا"}
            </small>
          </button>
        </div>
      )}
    </Shell>
  );
}

/** Applicants abroad: how far Saudi time is from their own clock. */
function localNote(tz: string) {
  const here = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (!here || here === tz) return "";
  const now = new Date();
  const offset = (zone: string) =>
    (new Date(now.toLocaleString("en-US", { timeZone: zone })).getTime() -
      new Date(now.toLocaleString("en-US", { timeZone: "UTC" })).getTime()) /
    3_600_000;
  const h = Math.round((offset(tz) - offset(here)) * 2) / 2;
  if (!h) return "";
  return `، وهو ${h > 0 ? "متقدم على" : "متأخر عن"} توقيتك بـ ${Math.abs(h).toLocaleString("ar-SA-u-nu-arab")} ساعة`;
}

export function Shell({ children, loading }: { children?: React.ReactNode; loading?: boolean }) {
  return (
    <main className="iv-page" dir="rtl">
      <div className="iv-sheet" aria-busy={loading}>
        {loading ? (
          <header className="iv-top">
            <img src="/logo-ivory.png" alt="مُبين" />
            <h1>لحظات…</h1>
          </header>
        ) : (
          children
        )}
      </div>
    </main>
  );
}

export function Closed() {
  return (
    <Shell>
      <header className="iv-top">
        <img src="/logo-ivory.png" alt="مُبين" />
      </header>
      <div className="iv-body iv-center">
        <div className="iv-check muted">
          <div>
            <Lock size={26} />
          </div>
        </div>
        <div className="iv-title">هذا الرابط لم يعد صالحًا</div>
        <p className="iv-sub">
          إن كنت حجزت موعدك، تجد رابط المقابلة في رسالة الواتساب. وإن احتجت موعدًا، تواصل مع الجمعية.
        </p>
      </div>
    </Shell>
  );
}
